import React, { useMemo } from 'react';
import { Row, Col, Card, Skeleton, ConfigProvider } from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  RiseOutlined,
  BoxPlotOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { orderPermissions, shopifyOrderPermissions } from '../../../utils/permissions';
import { FILTER_TYPES } from '../constants/filterTypes';

/**
 * KPI Dashboard for Shopify Orders
 */
const KpiDashboard = ({ metrics, loading, onFilterChange, onApplyFilters }) => {
  const { user } = useSelector((state) => state.auth);
  const canViewFinancialData = useMemo(() => orderPermissions.canViewFinancialData(user), [user]);
  const canViewBaseCost = useMemo(() => shopifyOrderPermissions.canViewBaseCost(user), [user]);

  const safeTotalItems = metrics?.totalItems || 0;
  const fulfilledCount = metrics?.fulfillmentBreakdown
    ? (Number(metrics.fulfillmentBreakdown.find((f) => f.fulfillmentStatus?.toLowerCase() === 'fulfilled')?.count || 0))
    : 0;
  // Backend explicitly counts NULL + 'Unfulfilled' + 'unfulfilled' items
  const unfulfilledCount = Number(metrics?.unfulfilledCount || 0);
  const fulfillmentRate = safeTotalItems > 0 ? (fulfilledCount / safeTotalItems) * 100 : 0;

  const totalRevenue = Number(metrics?.totalRevenue || 0);
  const totalShipping = Number(metrics?.totalShipping || 0);
  const totalSales = totalRevenue + totalShipping;
  const totalBaseCost = Number(metrics?.totalBaseCost || 0);
  const profit = Number(metrics?.profit ?? (totalRevenue - totalBaseCost) ?? 0);

  const kpiData = useMemo(() => {
    if (!metrics) return [];

    const all = [
      {
        title: 'Orders',
        value: metrics.totalOrders || 0,
        color: '#1890ff',
        icon: ShoppingCartOutlined,
        description: 'Total unique orders'
      },
      {
        title: 'Order Items',
        value: safeTotalItems,
        color: '#722ed1',
        icon: BoxPlotOutlined,
        description: 'Total line items'
      },
      {
        title: 'Sold Total',
        value: metrics.totalQuantity || 0,
        color: '#2f54eb',
        icon: BoxPlotOutlined,
        description: 'Sum of all quantities'
      },
      {
        title: 'Total Sales',
        value: totalSales,
        color: '#13c2c2',
        icon: RiseOutlined,
        description: 'Net Sales + Shipping',
        precision: 2,
        isFinancial: true
      },
      {
        title: 'Base Costs Total',
        value: totalBaseCost,
        color: '#fa541c',
        icon: DollarOutlined,
        description: 'Sum(baseCost × qty)',
        precision: 2,
        isBaseCost: true
      },
      {
        title: 'Profit Total',
        value: profit,
        color: profit >= 0 ? '#52c41a' : '#f5222d',
        icon: DollarOutlined,
        description: 'Revenue - Base costs',
        precision: 2,
        isFinancial: true
      },
      {
        title: 'Fulfillment Rate',
        value: fulfillmentRate,
        color: '#52c41a',
        icon: CheckCircleOutlined,
        description: 'Fulfilled / total items',
        precision: 1,
        suffix: '%'
      },
      {
        title: 'Unfulfilled',
        value: unfulfilledCount,
        color: '#faad14',
        icon: ClockCircleOutlined,
        description: 'Items need fulfillment',
        clickable: true,
        metricType: 'unfulfilled'
      }
    ];

    return all.filter((x) => {
      if (x.isFinancial && !canViewFinancialData) return false;
      if (x.isBaseCost && !canViewBaseCost) return false;
      return true;
    });
  }, [metrics, canViewFinancialData, canViewBaseCost, safeTotalItems, totalSales, totalBaseCost, profit, fulfillmentRate, unfulfilledCount]);

  // Handle KPI metric click to apply corresponding filter
  const handleKpiClick = (metricType) => {
    if (!onFilterChange || !onApplyFilters) return;

    switch (metricType) {
      case 'unfulfilled':
        onFilterChange(FILTER_TYPES.FULFILLMENT_STATUS, 'Unfulfilled');
        break;
      default:
        return;
    }

    onApplyFilters();
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Skeleton: {
            paragraphLiHeight: 10,
            paragraphMarginTop: 10,
            titleHeight: 10
          }
        }
      }}
    >
      <div>
        {loading ? (
          <Row gutter={[8, 8]}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Col xs={6} sm={4} md={4} lg={4} xl={4} key={index}>
                <Card
                  size="small"
                  style={{
                    background: '#fff',
                    borderRadius: '12px',
                    boxShadow: '2px 4px 9px -5px rgba(0,0,0,0.08)',
                    border: '1px solid #f5f5f5',
                    height: '72px'
                  }}
                  styles={{
                    body: {
                      padding: '5px 14px',
                      height: '100%'
                    }
                  }}
                >
                  <Skeleton active paragraph={{ rows: 1, width: '100%' }} title={{ width: '60%' }} />
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Row gutter={[8, 8]}>
            {kpiData.map((item, index) => {
              const IconComponent = item.icon;
              const displayValue = item.precision != null
                ? Number(item.value || 0).toFixed(item.precision)
                : Number(item.value || 0);

              return (
                <Col xs={6} sm={4} md={4} lg={4} xl={4} key={index}>
                  <Card
                    size="small"
                    style={{
                      background: '#fff',
                      borderRadius: '12px',
                      boxShadow: '2px 4px 9px -5px rgba(0,0,0,0.08)',
                      border: '1px solid #f5f5f5',
                      height: '72px',
                      cursor: item.clickable ? 'pointer' : 'default'
                    }}
                    styles={{
                      body: {
                        padding: '5px 14px',
                        height: '100%'
                      }
                    }}
                    onClick={item.clickable ? () => handleKpiClick(item.metricType) : undefined}
                    onMouseEnter={item.clickable ? (e) => {
                      e.currentTarget.style.border = '1px solid #1890ff';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.15)';
                    } : undefined}
                    onMouseLeave={item.clickable ? (e) => {
                      e.currentTarget.style.border = '1px solid #f5f5f5';
                      e.currentTarget.style.boxShadow = '2px 4px 9px -5px rgba(0,0,0,0.08)';
                    } : undefined}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px' }}>
                      <span style={{ color: '#8c8c8c', fontSize: '11px', fontWeight: '500' }}>{item.title}</span>
                      <IconComponent style={{ fontSize: '12px', color: item.color }} />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '3px', marginBottom: '2px' }}>
                      <span style={{ color: '#262626', fontSize: '17px', fontWeight: 'bold', lineHeight: '1.2' }}>
                        {Number(displayValue).toLocaleString()}
                      </span>
                      {item.suffix && (
                        <span style={{ color: '#8c8c8c', fontSize: '9px', fontWeight: '400' }}>{item.suffix}</span>
                      )}
                    </div>

                    <div style={{ fontSize: '11px', color: '#8c8c8c', fontWeight: '400' }}>{item.description}</div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>
    </ConfigProvider>
  );
};

export default KpiDashboard;
